/**
 * AuthContext.js — Week 4 (QA clean)
 * - Removed all console.log/warn/error calls (production-ready)
 * - Exposes userRole derived from useRoles normalisation
 * - Keeps exact same API surface: user, userData, tenant, features,
 *   loading, currentUser, isSystemAdmin, logout
 *
 * KEY FIX: tenant_features now uses onSnapshot (real-time listener)
 * instead of getDoc (one-time read). This means:
 *   1. Feature flag changes by admin take effect instantly for all
 *      logged-in users of that tenant — no refresh needed.
 *   2. The products_page_enabled toggle (and any other flag) propagates
 *      live across all active sessions.
 */
import { createContext, useState, useEffect, useContext } from "react";
import { auth, db } from "../firebaseConfig";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { normaliseRole, ROLES } from "../hooks/useRoles";

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

/* ── Default features (fallback when tenant_features doc missing) ── */
const DEFAULT_FEATURES = {
  products_page_enabled:      false,  // hidden by default — system admin enables per tenant
  campaign_decision_enabled:  true,
  share_enabled:              true,
  pdf_enabled:                false,
  analytics_enabled:          false,
  decision_mode_enabled:      false,
  api_access_enabled:         false,
  export_enabled:             false,
  custom_branding_enabled:    false,
};

export const AuthProvider = ({ children }) => {
  const [user,     setUser]     = useState(null);
  const [userData, setUserData] = useState(null);
  const [tenant,   setTenant]   = useState(null);
  const [features, setFeatures] = useState({});
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    // Holds the unsubscribe fn for the tenant_features snapshot listener
    let unsubscribeFeatures = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      // Clean up any previous features listener when auth state changes
      if (unsubscribeFeatures) {
        unsubscribeFeatures();
        unsubscribeFeatures = null;
      }

      // ── CRITICAL: reset loading on every auth transition ──
      // Without this, after the initial mount loading stays false forever.
      // When a user signs in, downstream consumers (ProtectedRoute,
      // CompanyDashboard, etc.) would see loading=false while userData/tenant
      // are still being fetched async — causing a flash of empty state
      // before the real page renders.
      setLoading(true);

      // ── Clear previous user's data immediately on transition ──
      // Prevents the previous user's userData/tenant/features from being
      // visible for a moment while the new user's data is fetched.
      setUserData(null);
      setTenant(null);
      setFeatures({});

      if (firebaseUser) {
        setUser(firebaseUser);
        try {
          const userSnap = await getDoc(doc(db, "users", firebaseUser.uid));
          if (userSnap.exists()) {
            const ud = { uid: firebaseUser.uid, email: firebaseUser.email, ...userSnap.data() };
            setUserData(ud);

            if (ud.tenantId) {
              // Load tenant (one-time — tenant settings rarely change)
              const tenantSnap = await getDoc(doc(db, "tenants", ud.tenantId));
              if (tenantSnap.exists()) setTenant({ id: ud.tenantId, ...tenantSnap.data() });

              // ── Real-time listener for feature flags ─────────────
              // onSnapshot fires immediately with current data, then
              // again whenever the document changes in Firestore.
              // This means admin flag changes propagate instantly to
              // all active sessions without any refresh.
              //
              // We await the FIRST snapshot before flipping loading=false
              // so the UI never renders with empty features (which would
              // briefly hide gated nav items, etc.).
              await new Promise((resolve) => {
                let resolved = false;
                unsubscribeFeatures = onSnapshot(
                  doc(db, "tenant_features", ud.tenantId),
                  (snap) => {
                    if (snap.exists()) {
                      setFeatures(snap.data().customFeatures || DEFAULT_FEATURES);
                    } else {
                      setFeatures(DEFAULT_FEATURES);
                    }
                    if (!resolved) { resolved = true; resolve(); }
                  },
                  () => {
                    // On error, fall back to defaults
                    setFeatures(DEFAULT_FEATURES);
                    if (!resolved) { resolved = true; resolve(); }
                  }
                );
              });
            }
          } else {
            setUserData(null);
          }
        } catch {
          setUserData(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeFeatures) unsubscribeFeatures();
    };
  }, []);

  const currentUser = userData
    ? {
        uid:         userData.uid,
        email:       userData.email,
        displayName: userData.displayName,
        tenantId:    userData.tenantId,
        role:        userData.role,
        status:      userData.status,
        features,
      }
    : null;

  const normalisedRole = normaliseRole(userData?.role);
  const isSystemAdmin  = normalisedRole === ROLES.SUPERADMIN;

  // ── Canonical plan identity ───────────────────────────────
  // The backend stores the plan in `tenants/{id}.planId` (with a `plan`
  // alias for older docs). For ages, parts of the dashboard read a
  // phantom `userData.subscriptionTier` field that was never written
  // anywhere — so the header on /company always read "free" while
  // /admin#plans correctly read "pro". We resolve once here so every
  // component sees the same answer.
  const planId =
    tenant?.planId           ||
    tenant?.plan             ||
    userData?.subscriptionTier ||  // legacy fallback only
    "free";

  const logout = async () => { await signOut(auth); };

  return (
    <AuthContext.Provider value={{ user, userData, tenant, features, loading, currentUser, isSystemAdmin, planId, logout }}>
      {children}
    </AuthContext.Provider>
  );
};